import type { Character } from "../types/character";
import type { HorrorRaceState } from "../types/game-state";
import type { Zone } from "../types/zone";
import { computeRaceRecap } from "../utils/race-recap";
import { calculateNextTurnOrder } from "../utils/turn-order";

/**
 * T26: advance past the round-end recap. The zone resolution + pawn moves were
 * already applied when the round resolved (ROUND_RECAP entry, see
 * applyResolveRound).
 *
 * Non-finishing recap: perform the next-round transition (recycle/rotate/round
 * + 1). Finishing recap: the round is the race's final round, so clear the
 * zones, compute the stable race recap, and hold in RACE_SCORING.
 */
export function applyAdvanceRound(state: HorrorRaceState): HorrorRaceState {
  const finishedRace =
    state.roundResolution?.steps.some((step) => step.finished) ?? false;

  if (finishedRace) {
    const clearedZones = { 1: [], 2: [], 4: [] } as Readonly<
      Record<Zone, readonly Character[]>
    >;
    const { raceResult, garnets } = computeRaceRecap(state);
    return {
      ...state,
      zones: clearedZones,
      phase: "RACE_SCORING",
      raceResults: [...state.raceResults, raceResult],
      garnets,
      roundResolution: null,
      players: state.players.map((p) => ({
        ...p,
        hasRevealed: true,
        hasConfirmedRecap: false,
        hasConfirmedRoundRecap: false,
      })),
    };
  }

  const recycledPool: readonly Character[] = [
    ...state.coinPool,
    ...state.zones[1],
    ...state.zones[2],
    ...state.zones[4],
  ];

  const nextTurnOrder = calculateNextTurnOrder(
    state.turnOrder,
    state.forfeitPlayerIndex,
  );

  return {
    ...state,
    zones: { 1: [], 2: [], 4: [] },
    coinPool: recycledPool,
    round: state.round + 1,
    turnOrder: nextTurnOrder,
    currentTurnIndex: 0,
    drawPass: 1,
    forfeitPlayerIndex: null,
    hasForfeitThisRound: false,
    drawnCoins: [],
    roundResolution: null,
    phase: "ROUND_DRAW_AND_PLACE",
    players: state.players.map((p) => ({
      ...p,
      hasConfirmedRoundRecap: false,
    })),
  };
}
